// Prescripciones clínicas (Fase 4, caso hospital). El menú se PRESCRIBE por paciente;
// alimenta el censo de dietas → la producción explota por dieta. Datos de salud (LOPDP):
// exige consentimiento; el diagnóstico solo se ve aquí. Catering → Operación.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stethoscope, ShieldCheck, ClipboardList, Check, X, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering } from './shared';

interface Contrato { id: string; nombre: string }
interface Paciente {
  comensal_id: string; codigo: string | null; nombre: string; consent_salud: boolean; partner_id: string | null;
  dieta_tipo_id: string | null; dieta: string | null; es_terapeutica: boolean | null;
  diagnostico: string | null; desde: string | null; hasta: string | null; prescrito_por: string | null;
}
interface Dieta { id: string; nombre: string; es_terapeutica: boolean }
type Editor = { comensal_id: string; nombre: string; dieta_tipo_id: string; desde: string; hasta: string; prescrito_por: string; diagnostico: string; notas: string };

const isoHoy = (): string => new Date().toISOString().slice(0, 10);

export function PrescripcionesCatering() {
  const qc = useQueryClient();
  const [contratoId, setContratoId] = useState('');
  const [fecha, setFecha] = useState(isoHoy());
  const [editor, setEditor] = useState<Editor | null>(null);

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos-presc'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=id,nombre&es_activo=is.true&deleted_at=is.null&order=nombre'),
  });
  const contrato = contratos.find((c) => c.id === contratoId) ?? contratos[0];
  const cid = contrato?.id ?? '';

  const { data: pacientes = [], isLoading } = useQuery<Paciente[]>({
    queryKey: ['cat-presc-pacientes', cid, fecha],
    queryFn: () => catRpc<Paciente[]>('cat_prescripcion_pacientes', { _contrato_id: cid, _fecha: fecha }),
    enabled: !!cid,
  });
  const { data: dietas = [] } = useQuery<Dieta[]>({ queryKey: ['cat-dietas-tipo'], queryFn: () => catRpc<Dieta[]>('cat_dieta_listar') });

  const invalidate = () => invalidateCatering(qc);
  const consent = useMutation({
    mutationFn: (p: { id: string; v: boolean }) => catRpc('cat_comensal_consent_set', { _comensal_id: p.id, _consent: p.v }),
    onSuccess: () => invalidate(), onError: (e) => toast.error(getErrorMessage(e)),
  });
  const prescribir = useMutation({
    mutationFn: (e: Editor) => catRpc('cat_prescripcion_guardar', {
      _comensal_id: e.comensal_id, _dieta_tipo_id: e.dieta_tipo_id, _desde: e.desde || null, _hasta: e.hasta || null,
      _prescrito_por: e.prescrito_por || null, _diagnostico: e.diagnostico || null, _notas: e.notas || null,
    }),
    onSuccess: () => { invalidate(); setEditor(null); toast.success('Prescripción registrada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const censar = useMutation({
    mutationFn: () => catRpc<{ lineas_censo: number; pacientes_terapeuticos: number }>('cat_prescripcion_censar', { _contrato_id: cid, _fecha: fecha }),
    onSuccess: (r) => { invalidate(); toast.success(`Censo actualizado: ${r.pacientes_terapeuticos} paciente(s) terapéutico(s) en ${r.lineas_censo} línea(s)`); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Stethoscope className="h-4 w-4" /> Prescripciones clínicas</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Contrato</label>
              <select className={`${fieldCls} w-72`} value={cid} onChange={(e) => setContratoId(e.target.value)}>
                {contratos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div><label className="block text-xs text-muted-foreground">Fecha</label><input type="date" className={`${fieldCls} w-40`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
            <Button size="sm" className="gap-1" disabled={censar.isPending} onClick={() => censar.mutate()}><ClipboardList className="h-4 w-4" /> Censar día</Button>
          </div>
          <div className="mt-2 flex items-start gap-2 rounded-md border border-sky-300 bg-sky-50/60 p-2 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-900/10 dark:text-sky-300">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Datos de salud (LOPDP): sin consentimiento del comensal no se puede prescribir. El diagnóstico solo se ve en esta pantalla; nunca pasa al censo, la producción, el kiosco ni el comedor. «Censar día» vuelca las dietas terapéuticas prescritas al censo para que la producción explote por dieta.</span>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-4 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Paciente</th>
                  <th className="py-1 pr-2 text-center">Consent.</th><th className="py-1 pr-2">Dieta vigente</th>
                  <th className="py-1 pr-2">Diagnóstico</th><th className="py-1 pr-2">Médico</th><th className="py-1 pr-2"></th>
                </tr></thead>
                <tbody>
                  {pacientes.map((p) => (
                    <tr key={p.comensal_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 font-mono text-xs">{p.codigo ?? '—'}</td>
                      <td className="py-1.5 pr-2 font-medium">{p.nombre}</td>
                      <td className="py-1.5 pr-2 text-center">
                        <button onClick={() => consent.mutate({ id: p.comensal_id, v: !p.consent_salud })} disabled={consent.isPending}
                          title={p.consent_salud ? 'Consentimiento otorgado' : 'Sin consentimiento'}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 ${p.consent_salud ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-transparent hover:border-emerald-400'}`}>
                          <ShieldCheck className="h-3.5 w-3.5" />
                        </button>
                      </td>
                      <td className="py-1.5 pr-2">{p.dieta ? <span className={p.es_terapeutica ? 'font-medium text-amber-700 dark:text-amber-400' : ''}>{p.dieta}</span> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{p.diagnostico ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{p.prescrito_por ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-right">
                        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={!p.consent_salud}
                          title={p.consent_salud ? 'Prescribir dieta' : 'Requiere consentimiento'}
                          onClick={() => setEditor({ comensal_id: p.comensal_id, nombre: p.nombre, dieta_tipo_id: p.dieta_tipo_id ?? '', desde: fecha, hasta: '', prescrito_por: p.prescrito_por ?? '', diagnostico: p.diagnostico ?? '', notas: '' })}>
                          <Stethoscope className="h-3.5 w-3.5" /> Prescribir
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!pacientes.length && <tr><td colSpan={7} className="py-3 text-sm text-muted-foreground">Sin comensales en el padrón de este contrato. Cárgalos en Comensales.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Formulario de prescripción */}
      {editor && (
        <Card className="border-primary">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Prescribir dieta — {editor.nombre}</CardTitle></CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            <div><label className="block text-xs text-muted-foreground">Dieta *</label>
              <select className={`${fieldCls} w-full`} value={editor.dieta_tipo_id} onChange={(e) => setEditor({ ...editor, dieta_tipo_id: e.target.value })}>
                <option value="">— elegir —</option>
                {dietas.map((d) => <option key={d.id} value={d.id}>{d.nombre}{d.es_terapeutica ? ' (terapéutica)' : ''}</option>)}
              </select>
            </div>
            <div><label className="block text-xs text-muted-foreground">Médico</label><input className={`${fieldCls} w-full`} value={editor.prescrito_por} onChange={(e) => setEditor({ ...editor, prescrito_por: e.target.value })} /></div>
            <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-full`} value={editor.desde} onChange={(e) => setEditor({ ...editor, desde: e.target.value })} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta (opcional)</label><input type="date" className={`${fieldCls} w-full`} value={editor.hasta} onChange={(e) => setEditor({ ...editor, hasta: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="block text-xs text-muted-foreground">Diagnóstico (dato clínico sensible)</label><input className={`${fieldCls} w-full`} value={editor.diagnostico} onChange={(e) => setEditor({ ...editor, diagnostico: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="block text-xs text-muted-foreground">Notas</label><input className={`${fieldCls} w-full`} value={editor.notas} onChange={(e) => setEditor({ ...editor, notas: e.target.value })} /></div>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <Button size="sm" className="gap-1" disabled={prescribir.isPending || !editor.dieta_tipo_id} onClick={() => prescribir.mutate(editor)}><Check className="h-4 w-4" /> Prescribir</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditor(null)}><X className="h-4 w-4" /> Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
